<template>
  <div class="customer-category-mapping-config">
    <div class="page-bar">
      <div class="page-bar__context">
        <div
          v-for="field in state.contextFields"
          :key="field.key"
          class="context-item"
        >
          <span>{{ field.label }}：</span>
          <a-select
            v-model:value="state.searchParams[field.key]"
            style="width: 120px"
            @change="state.onContextChange"
          >
            <a-select-option
              v-for="item in state[field.options]"
              :key="item.id"
              :value="item.id"
            >
              {{ item.name }}
            </a-select-option>
          </a-select>
        </div>
      </div>
      <div>
        <a-button class="mr10" @click="state.openImport">导入</a-button>
        <a-button type="primary" @click="state.openCreate">+ 新建配置</a-button>
      </div>
    </div>

    <div class="ims-container">
      <div class="filter-bar">
        <div class="filter-bar__left">
          <span>状态：</span>
          <a-select
            v-model:value="state.filters.status"
            class="mr10"
            style="width: 120px"
          >
            <a-select-option value="">全部</a-select-option>
            <a-select-option value="1">已启用</a-select-option>
            <a-select-option value="0">草稿</a-select-option>
            <a-select-option value="2">已停用</a-select-option>
          </a-select>
          <a-input
            v-model:value="state.filters.keyword"
            placeholder="搜索映射名称或类目树..."
            style="width: 240px"
            @pressEnter="state.search"
          />
        </div>
        <div>
          <a-button type="primary" class="mr10" @click="state.search">
            查询
          </a-button>
          <a-button @click="state.reset">重置</a-button>
        </div>
      </div>

      <a-table
        row-key="id"
        bordered
        :columns="state.columns"
        :data-source="state.dataSource"
        :loading="state.loading"
        :pagination="false"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'index'">
            {{ record.id }}
          </template>
          <template v-else-if="column.dataIndex === 'mappingName'">
            <div class="primary-text">{{ record.mappingName }}</div>
          </template>
          <template v-else-if="column.dataIndex === 'treeName'">
            <a-tag color="blue">{{ record.treeName || '-' }}</a-tag>
          </template>
          <template v-else-if="column.dataIndex === 'status'">
            <a-tag :color="state.statusColor(record.status)">
              {{ state.statusText(record.status) }}
            </a-tag>
          </template>
          <template v-else-if="column.dataIndex === 'coverageRate'">
            {{ state.coverageText(record) }}
          </template>
          <template v-else-if="column.dataIndex === 'action'">
            <a class="mr10" @click="state.edit(record)">编辑</a>
            <a
              class="mr10"
              :class="{ 'action-disabled': state.exportingId === record.id }"
              @click="state.exportRow(record)"
            >
              {{ state.exportingId === record.id ? '导出中...' : '导出' }}
            </a>
            <a-popconfirm
              v-if="record.status === 1"
              title="确认停用该映射吗？"
              @confirm="state.changeStatus(record, 2)"
            >
              <a>停用</a>
            </a-popconfirm>
            <a-popconfirm
              v-else
              title="确认启用该映射吗？"
              @confirm="state.changeStatus(record, 1)"
            >
              <a>启用</a>
            </a-popconfirm>
            <a-popconfirm
              v-if="record.status !== 1"
              title="确认删除该映射吗？"
              @confirm="state.remove(record)"
            >
              <a class="danger-link ml10">删除</a>
            </a-popconfirm>
            <a class="ml10" @click="state.openCopy(record)">复制</a>
          </template>
        </template>
      </a-table>

      <div v-if="state.pagination.total" class="table-footer">
        <a-pagination
          :current="state.pagination.pageNum"
          :page-size="state.pagination.pageSize"
          :total="state.pagination.total"
          show-size-changer
          show-quick-jumper
          :show-total="total => `共 ${total} 条`"
          @change="state.pageChange"
        />
      </div>
    </div>

    <a-modal
      v-model:visible="state.createVisible"
      title="新建系统类目映射"
      :footer="null"
      @cancel="state.closeCreate"
    >
      <a-form layout="vertical">
        <a-form-item label="映射名称" required>
          <a-input
            v-model:value="state.createForm.mappingName"
            placeholder="如：标准映射、夏季映射"
          />
        </a-form-item>
        <a-form-item label="描述">
          <a-textarea
            v-model:value="state.createForm.description"
            :rows="3"
            placeholder="选填，方便团队识别用途"
          />
        </a-form-item>
        <a-form-item label="绑定对客类目树" required>
          <a-select
            v-model:value="state.createForm.treeId"
            placeholder="请选择对客类目树"
            :loading="state.treeLoading"
            :not-found-content="state.treeLoading ? '加载中...' : '暂无数据'"
          >
            <a-select-option
              v-for="item in state.treeOptions"
              :key="item.id"
              :value="item.id"
            >
              {{ item.treeName }}
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-form>
      <div class="modal-footer">
        <a-button @click="state.closeCreate">取消</a-button>
        <a-button
          type="primary"
          :loading="state.submitting"
          @click="state.confirmCreate"
        >
          创建
        </a-button>
      </div>
    </a-modal>

    <a-modal
      v-model:visible="state.copyVisible"
      title="复制系统类目映射"
      :footer="null"
      :mask-closable="!state.copying"
      @cancel="state.closeCopy"
    >
      <a-form layout="vertical">
        <a-form-item label="复制后的映射名称" required>
          <a-input
            v-model:value="state.copyForm.mappingName"
            :maxlength="100"
            placeholder="请输入复制后的映射名称"
            @pressEnter="state.confirmCopy"
          />
        </a-form-item>
      </a-form>
      <div class="modal-footer">
        <a-button :disabled="state.copying" @click="state.closeCopy">
          取消
        </a-button>
        <a-button
          type="primary"
          :loading="state.copying"
          @click="state.confirmCopy"
        >
          确认复制
        </a-button>
      </div>
    </a-modal>

    <a-modal
      v-model:visible="state.importVisible"
      title="导入对客类目映射"
      :footer="null"
      :width="560"
      :mask-closable="!state.importing"
      @cancel="state.closeImport"
    >
      <div class="mapping-import">
        <a-alert
          class="mapping-import__alert"
          type="info"
          show-icon
          message="请先在映射列表中导出映射文件，再使用导出的 Excel 文件进行导入。"
        />
        <a-form layout="vertical">
          <a-form-item label="映射名称" required>
            <a-input
              v-model:value="state.importForm.mappingName"
              :maxlength="100"
              placeholder="请输入映射名称"
            />
          </a-form-item>
          <a-form-item label="描述">
            <a-textarea
              v-model:value="state.importForm.description"
              :rows="3"
              placeholder="选填，方便团队识别用途"
            />
          </a-form-item>
          <a-form-item label="绑定对客类目树" required>
            <a-select
              v-model:value="state.importForm.treeId"
              placeholder="请选择对客类目树"
            >
              <a-select-option
                v-for="item in state.treeOptions"
                :key="item.id"
                :value="item.id"
              >
                {{ item.treeName }}
              </a-select-option>
            </a-select>
          </a-form-item>
          <a-form-item label="Excel 文件" required>
            <div class="mapping-import__upload-header">
              <span>请上传已导出的映射文件</span>
            </div>
            <a-upload
              :before-upload="() => false"
              :file-list="state.importFileList"
              :max-count="1"
              accept=".xlsx,.xls"
              @change="state.handleImportFileChange"
            >
              <a-button>选择 Excel 文件</a-button>
            </a-upload>
            <div class="mapping-import__hint">
              仅支持 `.xlsx`、`.xls` 格式。
            </div>
          </a-form-item>
        </a-form>
        <div class="modal-footer">
          <a-button :disabled="state.importing" @click="state.closeImport">
            取消
          </a-button>
          <a-button
            type="primary"
            :loading="state.importing"
            @click="state.confirmImport"
          >
            确认导入
          </a-button>
        </div>
      </div>
    </a-modal>

    <a-modal
      v-model:visible="state.importErrorVisible"
      title="导入校验失败"
      :width="680"
      ok-text="我知道了"
      :cancel-button-props="{ style: { display: 'none' } }"
      @ok="state.importErrorVisible = false"
    >
      <a-alert
        class="mapping-import__error-summary"
        type="error"
        show-icon
        :message="
          `共发现 ${state.importErrors.length} 条错误，请修改 Excel 后重新导入。`
        "
      />
      <a-table
        row-key="errorKey"
        size="small"
        bordered
        :columns="state.importErrorColumns"
        :data-source="state.importErrors"
        :pagination="false"
        :scroll="{ y: 360 }"
      />
    </a-modal>
  </div>
</template>

<script>
import { onMounted, reactive, toRefs } from 'vue'
import { useRouter } from 'vue-router'
import { message } from 'ant-design-vue'
import useAPI from 'UseApi'
import { postMessageToParent } from 'cupshe-utils'
import {
  getCustomerCategoryContext,
  setCustomerCategoryContext
} from '@/utils/customerCategoryContextCache'
import { listCustomerCategoryTrees } from '../CustomerCategoryTreeConfig/service'
import {
  changeCustomerCategoryMappingStatus,
  copyCustomerCategoryMapping,
  createCustomerCategoryMapping,
  deleteCustomerCategoryMapping,
  exportCustomerCategoryMapping,
  importCustomerCategoryMapping,
  listCustomerCategoryMappings,
  mappingStatusText
} from './service'

const { common_querySiteInfo_get_ } = useAPI(null, [3426])

const getRequestErrorMessage = (error, fallback) =>
  error?.retInfo ||
  error?.response?.data?.retInfo ||
  error?.data?.retInfo ||
  error?.message ||
  fallback

export default {
  name: 'CustomerCategoryMappingConfig',
  setup() {
    const router = useRouter()
    const state = reactive({
      loading: false,
      submitting: false,
      exportingId: '',
      importVisible: false,
      importing: false,
      importErrorVisible: false,
      importErrors: [],
      importErrorColumns: [
        {
          title: 'Excel 行号',
          dataIndex: 'rowNumber',
          width: 100,
          align: 'center'
        },
        { title: '字段', dataIndex: 'field', width: 160 },
        { title: '错误原因', dataIndex: 'message' }
      ],
      importFileList: [],
      importForm: {
        mappingName: '',
        description: '',
        treeId: undefined
      },
      dataSource: [],
      treeOptions: [],
      treeLoading: false,
      brands: [],
      channels: [],
      sites: [],
      terminals: [],
      contextFields: [
        { key: 'brandId', label: '品牌', options: 'brands' },
        { key: 'channelId', label: '渠道', options: 'channels' },
        { key: 'siteId', label: '站点', options: 'sites' }
      ],
      searchParams: getCustomerCategoryContext(),
      filters: { status: '', keyword: '' },
      pagination: { pageNum: 1, pageSize: 10, total: 0 },
      columns: [
        { title: '序号', dataIndex: 'index', width: 70, align: 'center' },
        { title: '映射名称', dataIndex: 'mappingName' },
        { title: '绑定客类目树', dataIndex: 'treeName', align: 'center' },
        { title: '状态', dataIndex: 'status', width: 90, align: 'center' },
        { title: '覆盖率', dataIndex: 'coverageRate', align: 'center' },
        { title: '启用时间', dataIndex: 'enableTime', align: 'center' },
        { title: '操作项', dataIndex: 'action', width: 350, align: 'center' }
      ],
      createVisible: false,
      copyVisible: false,
      copyRecord: null,
      copyForm: { mappingName: '' },
      copying: false,
      createForm: { mappingName: '', description: '', treeId: undefined },
      statusText: mappingStatusText,
      statusColor: status =>
        ({ 0: 'orange', 1: 'green', 2: 'default' }[status]),
      coverageText: record => {
        if (record.coverageRate !== undefined && record.coverageRate !== null) {
          const rate = Number(record.coverageRate)
          const percent = rate <= 1 ? rate * 100 : rate
          const counts = record.totalCount
            ? ` (${record.mappedCount}/${record.totalCount})`
            : ''
          return `${percent.toFixed(2)}%${counts}`
        }
        return `${record.mappedCount}/${record.totalCount}`
      },
      loadTrees: async () => {
        state.treeLoading = true
        state.treeOptions = []
        try {
          const result = await listCustomerCategoryTrees({
            ...state.searchParams,
            status: '0',
            pageNum: 1,
            pageSize: 9999
          })
          state.treeOptions = result.list
        } finally {
          state.treeLoading = false
        }
      },
      loadList: async () => {
        state.loading = true
        try {
          const result = await listCustomerCategoryMappings({
            ...state.searchParams,
            ...state.filters,
            ...state.pagination
          })
          state.dataSource = result.list.map(item => ({
            ...item,
            treeName:
              item.treeName ||
              state.treeOptions.find(tree => `${tree.id}` === `${item.treeId}`)
                ?.treeName ||
              `#${item.treeId}`
          }))
          state.pagination.total = result.total
        } catch (error) {
          message.error(error.message || '映射列表加载失败')
        } finally {
          state.loading = false
        }
      },
      onContextChange: async () => {
        setCustomerCategoryContext(state.searchParams)
        state.pagination.pageNum = 1
        try {
          await state.loadTrees()
          await state.loadList()
        } catch (error) {
          message.error(error.message || '上下文数据加载失败')
        }
      },
      search: () => {
        state.pagination.pageNum = 1
        state.loadList()
      },
      reset: () => {
        state.filters = { status: '', keyword: '' }
        state.pagination.pageNum = 1
        state.loadList()
      },
      pageChange: (page, pageSize) => {
        state.pagination.pageNum =
          pageSize === state.pagination.pageSize ? page : 1
        state.pagination.pageSize = pageSize
        state.loadList()
      },
      openCreate: async () => {
        state.createForm = {
          mappingName: '',
          description: '',
          treeId: undefined
        }
        state.createVisible = true
        try {
          await state.loadTrees()
        } catch (error) {
          message.error(error.message || '对客类目树加载失败')
        }
      },
      closeCreate: () => {
        state.createVisible = false
      },
      confirmCreate: async () => {
        if (!state.createForm.mappingName || !state.createForm.treeId) {
          message.error('请填写映射名称并选择对客类目树')
          return
        }
        state.submitting = true
        try {
          const created = await createCustomerCategoryMapping({
            brandId: state.searchParams.brandId,
            siteId: state.searchParams.siteId,
            ...state.createForm
          })
          message.success('映射创建成功')
          state.createVisible = false
          router.push({
            name: 'CustomerCategoryMappingConfigDetail',
            query: { id: created.id }
          })
        } catch (error) {
          message.error(error.message || '映射创建失败')
        } finally {
          state.submitting = false
        }
      },
      openCopy: record => {
        state.copyRecord = record
        state.copyForm = {
          mappingName: `复制-${record.mappingName || ''}`.slice(0, 100)
        }
        state.copyVisible = true
      },
      closeCopy: () => {
        if (state.copying) return
        state.copyVisible = false
        state.copyRecord = null
        state.copyForm = { mappingName: '' }
      },
      confirmCopy: async () => {
        if (!state.copyRecord || state.copying) return
        const mappingName = `${state.copyForm.mappingName || ''}`.trim()
        if (!mappingName) {
          message.error('请输入复制后的映射名称')
          return
        }
        state.copying = true
        try {
          await copyCustomerCategoryMapping(state.copyRecord.id, mappingName)
          message.success('映射复制成功')
          state.copyVisible = false
          state.copyRecord = null
          state.copyForm = { mappingName: '' }
          await state.loadList()
        } catch (error) {
          message.error(error.message || '映射复制失败')
        } finally {
          state.copying = false
        }
      },
      edit: record => {
        router.push({
          name: 'CustomerCategoryMappingConfigDetail',
          query: { id: record.id }
        })
      },
      exportRow: async record => {
        if (state.exportingId) return
        state.exportingId = record.id
        try {
          await exportCustomerCategoryMapping(record.id)
          postMessageToParent({
            type: 'exportNum',
            value: ''
          })
          message.success('导出成功，请前往右上角下载中心下载')
        } catch (error) {
          message.error(error.message || '导出任务提交失败')
        } finally {
          state.exportingId = ''
        }
      },
      openImport: () => {
        state.importForm = {
          mappingName: '',
          description: '',
          treeId: undefined
        }
        state.importFileList = []
        state.importErrors = []
        state.importVisible = true
      },
      closeImport: () => {
        if (state.importing) return
        state.importFileList = []
        state.importVisible = false
      },
      handleImportFileChange: ({ fileList }) => {
        state.importFileList = fileList.slice(-1)
      },
      confirmImport: async () => {
        if (state.importing) return
        const mappingName = `${state.importForm.mappingName || ''}`.trim()
        if (!mappingName || !state.importForm.treeId) {
          message.error('请填写映射名称并选择对客类目树')
          return
        }
        const uploadFile = state.importFileList[0]
        const file = uploadFile?.originFileObj || uploadFile
        if (!file) {
          message.error('请选择要导入的 Excel 文件')
          return
        }
        if (!/\.xlsx?$/i.test(file.name || '')) {
          message.error('仅支持 .xlsx、.xls 格式')
          return
        }
        state.importing = true
        try {
          const result = await importCustomerCategoryMapping({
            ...state.searchParams,
            ...state.importForm,
            mappingName,
            file
          })
          const errors = Array.isArray(result?.errors) ? result.errors : []
          if (errors.length) {
            state.importErrors = errors.map((item, index) => ({
              ...item,
              rowNumber: item.rowNumber ?? '-',
              field: item.field || '-',
              message: item.message || '未知错误',
              errorKey: `${item.rowNumber ?? 'unknown'}-${item.field ||
                'field'}-${index}`
            }))
            state.importErrorVisible = true
            message.error(`导入校验失败，共 ${errors.length} 条错误`)
            return
          }
          message.success('导入成功')
          state.importVisible = false
          state.importFileList = []
          await state.loadList()
        } catch (error) {
          message.error(
            getRequestErrorMessage(error, '导入失败，请检查文件内容')
          )
        } finally {
          state.importing = false
        }
      },
      changeStatus: async (record, status) => {
        try {
          await changeCustomerCategoryMappingStatus(record.id, {
            brandId: state.searchParams.brandId,
            siteId: state.searchParams.siteId,
            status
          })
          message.success(status === 1 ? '映射已启用' : '映射已停用')
          await state.loadList()
        } catch (error) {
          message.error(error.message || '状态切换失败')
        }
      },
      remove: async record => {
        try {
          await deleteCustomerCategoryMapping(record.id)
          message.success('映射删除成功')
          await state.loadList()
        } catch (error) {
          message.error(error.message || '映射删除失败')
        }
      }
    })

    onMounted(async () => {
      await common_querySiteInfo_get_({ state })
      try {
        await state.loadTrees()
        await state.loadList()
      } catch (error) {
        message.error(error.message || '页面初始化失败')
      }
    })

    return { ...toRefs(state), state }
  }
}
</script>

<style lang="scss" scoped>
.customer-category-mapping-config {
  min-height: 100vh;
  padding: 16px;
  background: #f5f7fa;
}

.page-bar,
.filter-bar,
.page-bar__context,
.filter-bar__left,
.modal-footer {
  display: flex;
  align-items: center;
}

.page-bar,
.filter-bar {
  justify-content: space-between;
}

.page-bar {
  padding: 16px 20px;
  background: #fff;
}

.context-item {
  margin-right: 18px;
}

.filter-bar {
  margin-bottom: 16px;
}

.table-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

.modal-footer {
  justify-content: flex-end;
  gap: 8px;
}

.primary-text {
  font-weight: 500;
  color: #1f2329;
}

.secondary-text {
  margin-top: 4px;
  color: #8c8c8c;
  font-size: 12px;
}

.danger-link {
  color: #ff4d4f;
}

.action-disabled {
  color: #bfbfbf;
  cursor: not-allowed;
}

.mapping-import {
  &__alert {
    margin-bottom: 20px;
  }

  &__upload-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  &__hint {
    margin-top: 8px;
    color: #999;
    font-size: 12px;
  }

  &__error-summary {
    margin-bottom: 16px;
  }
}

.mr10 {
  margin-right: 10px;
}

.ml10 {
  margin-left: 10px;
}
</style>
